import {
  HOLIDAY_PROMO_CODE,
  HOLIDAY_PROMO_CONFIG,
  HOLIDAY_PROMO_TYPE,
  HolidayPromoConfig,
  MonthsOffPromoConfig,
  PROMO_TYPE,
  discountedPlanMapping,
  priceMapping,
  MONTHS_OFF_PROMO_REPLACEMENT_CODES,
} from "./holidayPromoConstants";
import { getRouteId } from "./meristemContext";
import { getUserCurrency } from "./plans";
import { formatPrice } from "./priceUtils";
import { getURLParams } from "./urlParams";

function checkCustomUrlParamsMatch(
  customParams: Record<string, string>,
  urlParams: Record<string, string>
): boolean {
  return Object.entries(customParams).every(([key, expectedValue]) => {
    const actualValue = urlParams[key];
    return actualValue === expectedValue;
  });
}

export function isHolidayPromotion() {
  return getCurrentHolidayPromoType() !== undefined;
}

export function shouldHidePromoCodeOnCheckout() {
  const holidayPromoConfig = getCurrentHolidayPromoConfig();
  return holidayPromoConfig?.hideCodeOnCheckout === true;
}

export function getHolidayPromoCode() {
  const holidayPromoConfig = getCurrentHolidayPromoConfig();
  return holidayPromoConfig?.uiPromoCode;
}

export function shouldAutoApplyHolidayPromoCode() {
  const holidayPromoConfig = getCurrentHolidayPromoConfig();
  return holidayPromoConfig?.autoApplyPromoCode;
}

export function getCurrentHolidayPromoType(promoCode?: HOLIDAY_PROMO_CODE) {
  return promoCode
    ? getHolidayPromoTypeForPromoCode(promoCode)
    : getEligibleHolidayPromoType();
}

export function getCurrentHolidayPromoConfig(promoCode?: HOLIDAY_PROMO_CODE) {
  const promoType = getCurrentHolidayPromoType(promoCode);
  return HOLIDAY_PROMO_CONFIG[promoType];
}

export function isFreeMonthsPromoConfig(
  promoConfig: HolidayPromoConfig
): promoConfig is MonthsOffPromoConfig {
  return promoConfig?.promoType === PROMO_TYPE.MONTHS_OFF;
}

export function isHolidayPromoCode(
  initialPromoCode: string
): initialPromoCode is HOLIDAY_PROMO_CODE {
  return initialPromoCode in HOLIDAY_PROMO_CODE;
}

export function canApplyHolidayPromoCode(promoCode: HOLIDAY_PROMO_CODE) {
  const holidayPromoConfig = getCurrentHolidayPromoConfig(promoCode);
  if (holidayPromoConfig) {
    return (
      holidayPromoConfig.isUserSegmentEligible() &&
      holidayPromoConfig.eligibleRoutes.includes(getRouteId())
    );
  }

  return false;
}

function isHolidayPromoDateRangeValid(
  holidayPromoConfig: HolidayPromoConfig
): boolean {
  const currentTime = new Date();
  return (
    currentTime >= holidayPromoConfig.activeFrom &&
    currentTime < holidayPromoConfig.activeTo
  );
}

function isHolidayPromoEligible(promoType: HOLIDAY_PROMO_TYPE): boolean {
  const config = HOLIDAY_PROMO_CONFIG[promoType];

  if (!config.enabled) {
    return false;
  }

  if (config.activeFrom && config.activeTo) {
    if (!isHolidayPromoDateRangeValid(config)) {
      return false;
    }
  }

  return true;
}

export function getEligibleHolidayPromoType(): HOLIDAY_PROMO_TYPE {
  const urlParams = getURLParams();
  const promo = urlParams?.promo;

  const eligiblePromoType = Object.keys(HOLIDAY_PROMO_TYPE)
    .filter((promoType) =>
      isHolidayPromoEligible(promoType as HOLIDAY_PROMO_TYPE)
    )
    .find((promoType) => {
      const config = HOLIDAY_PROMO_CONFIG[promoType];
      const baseEligibilityCheck =
        config.isUserSegmentEligible() &&
        config.eligibleRoutes.includes(getRouteId());

      if (!baseEligibilityCheck) return false;

      let isEligiblePromo = true;

      if (config.urlPromoValue) {
        isEligiblePromo = isEligiblePromo && config.urlPromoValue === promo;
      }

      if (config.customUrlParams) {
        isEligiblePromo =
          isEligiblePromo &&
          checkCustomUrlParamsMatch(config.customUrlParams, urlParams);
      }

      return isEligiblePromo;
    });

  return eligiblePromoType as HOLIDAY_PROMO_TYPE;
}

export function getHolidayPromoTypeForPromoCode(
  promoCode: HOLIDAY_PROMO_CODE
): HOLIDAY_PROMO_TYPE {
  const matchingPromoType = Object.keys(HOLIDAY_PROMO_TYPE)
    .filter((promoType) =>
      isHolidayPromoEligible(promoType as HOLIDAY_PROMO_TYPE)
    )
    .find((promoType) => {
      const config = HOLIDAY_PROMO_CONFIG[promoType];
      return config.uiPromoCode === promoCode;
    });

  return matchingPromoType as HOLIDAY_PROMO_TYPE;
}

export function getHolidayPromoForcedPlanDuration(
  promoCode?: HOLIDAY_PROMO_CODE
): number | undefined {
  const promoConfig = getCurrentHolidayPromoConfig(promoCode);

  return isFreeMonthsPromoConfig(promoConfig)
    ? promoConfig.forcedPlanDuration
    : undefined;
}

export function getHolidayPromoNumberOfMonthsOff(
  promoCode?: HOLIDAY_PROMO_CODE
): number | undefined {
  const promoConfig = getCurrentHolidayPromoConfig(promoCode);

  return isFreeMonthsPromoConfig(promoConfig)
    ? promoConfig.numberOfMonthsOff
    : undefined;
}

export function getHolidayPromoForcedPlanPrice() {
  const currency = getUserCurrency();
  const promoConfig = getCurrentHolidayPromoConfig();

  let mapping = discountedPlanMapping;
  let planDuration = 12;

  if (isFreeMonthsPromoConfig(promoConfig)) {
    mapping = priceMapping;
    planDuration = promoConfig.forcedPlanDuration;
  }

  const rawPrice = mapping[currency]?.[planDuration];
  return formatPrice(rawPrice, currency);
}

export function getHolidayReplacementPromoCode(
  promoCode: HOLIDAY_PROMO_CODE,
  currency: string
) {
  const config = getCurrentHolidayPromoConfig(promoCode);

  if (isFreeMonthsPromoConfig(config)) {
    const monthsOff = config.numberOfMonthsOff;
    return MONTHS_OFF_PROMO_REPLACEMENT_CODES[currency]?.[monthsOff];
  }
  return undefined;
}
